/**
 * The file-type icon of a library row. A Word entry made from an original PDF
 * shows the Word icon with a small PDF mark in its corner, so the table says
 * at a glance that the agents edit a docx whose source was a PDF.
 */
import type { FC } from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { cn } from "@/lib/utils";

import { convertedFromPdf, formatLabel, PDF_MIME } from "../model";
import type { FirmLibraryEntry } from "../types";

type Props = {
  entry: Pick<
    FirmLibraryEntry,
    "mime_type" | "original_filename" | "is_pdf" | "authorable" | "conversion"
  >;
  size?: number;
  className?: string;
};

/** A filename `FileTypeIcon` can resolve. `is_pdf` wins over a `.docx` name. */
const iconFileName = (entry: Props["entry"]): string => {
  if (entry.is_pdf) return "document.pdf";
  const name = entry.original_filename?.trim();
  if (name) return name;
  if (entry.authorable) return "document.docx";
  return "document";
};

export const libraryFileIconLabel = (entry: Props["entry"]): string =>
  convertedFromPdf(entry)
    ? "Word document, converted from PDF"
    : formatLabel(entry);

export const LibraryFileIcon: FC<Props> = ({ entry, size = 22, className }) => {
  const converted = convertedFromPdf(entry);
  const label = libraryFileIconLabel(entry);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn("relative inline-flex shrink-0", className)}
      data-testid="library-file-icon"
      data-converted={converted || undefined}
    >
      <FileTypeIcon
        name={iconFileName(entry)}
        mimeType={entry.is_pdf ? PDF_MIME : entry.mime_type}
        size={size}
      />
      {converted ? (
        <span
          className="absolute -bottom-1 -right-1.5 rounded-xs bg-background p-px shadow-sm ring-1 ring-border"
          data-testid="library-file-icon-pdf"
        >
          <FileTypeIcon
            name="source.pdf"
            mimeType={PDF_MIME}
            size={Math.max(10, Math.round(size * 0.5))}
          />
        </span>
      ) : null}
    </span>
  );
};
